// Copyright 2026 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Avatar, Button, Card, Col, Divider, Result, Row, Spin, Typography} from "antd";
import {
  ClockCircleOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined
} from "@ant-design/icons";
import * as AuthBackend from "./AuthBackend";
import * as Setting from "../Setting";
import i18next from "i18next";
import {withRouter} from "react-router-dom";

const {Title, Text} = Typography;

const SCOPE_METADATA = {
  openid: {icon: IdcardOutlined, titleKey: "consent:Scope openid title", descKey: "consent:Scope openid desc"},
  profile: {icon: UserOutlined, titleKey: "consent:Scope profile title", descKey: "consent:Scope profile desc"},
  email: {icon: MailOutlined, titleKey: "consent:Scope email title", descKey: "consent:Scope email desc"},
  phone: {icon: PhoneOutlined, titleKey: "consent:Scope phone title", descKey: "consent:Scope phone desc"},
  address: {icon: EnvironmentOutlined, titleKey: "consent:Scope address title", descKey: "consent:Scope address desc"},
  offline_access: {icon: ClockCircleOutlined, titleKey: "consent:Scope offline_access title", descKey: "consent:Scope offline_access desc"},
};

/* eslint-disable no-console */
class ConsentPage extends React.Component {
  constructor(props) {
    super(props);
    const queries = new URLSearchParams(window.location.search);
    // Build oAuthParams from current URL (not using Util.getOAuthGetParameters which doesn't include code)
    const oAuthParams = {
      clientId: queries.get("client_id") || "",
      responseType: queries.get("response_type") || "",
      redirectUri: queries.get("redirect_uri") || "",
      scope: queries.get("scope") || "",
      state: queries.get("state") || "",
      nonce: queries.get("nonce") || "",
      challengeMethod: queries.get("code_challenge_method") || "",
      codeChallenge: queries.get("code_challenge") || "",
      noRedirect: queries.get("noRedirect") || "",
      type: "code",
    };
    const code = queries.get("code");
    const popup = queries.get("popup");

    console.log("[ConsentPage] constructor - URL params:", {
      oAuthParams,
      code,
      popup,
      search: window.location.search,
    });

    this.state = {
      classes: props,
      application: undefined,
      account: null,
      oAuthParams: oAuthParams,
      code: code,
      popup: popup,
      invalid: false,
    };
  }

  UNSAFE_componentWillMount() {
    const {oAuthParams, code} = this.state;
    console.log("[ConsentPage] componentWillMount - checking validity:", {
      hasOAuthParams: oAuthParams !== null,
      clientId: oAuthParams?.clientId,
      hasCode: code !== null,
      redirectUri: oAuthParams?.redirectUri,
      state: oAuthParams?.state,
    });

    if (oAuthParams === null || !oAuthParams.clientId || code === null || !oAuthParams.redirectUri || !oAuthParams.state) {
      console.error("[ConsentPage] Invalid parameters, missing required fields");
      this.setState({invalid: true});
      return;
    }
    this.getApplicationLogin();
    this.getAccountInfo();
  }

  getApplicationLogin() {
    AuthBackend.getApplicationLogin(this.state.oAuthParams).then((res) => {
      if (res.status === "ok") {
        this.setState({application: res.data});
      } else {
        this.setState({application: null});
      }
    });
  }

  getAccountInfo() {
    AuthBackend.getAccount().then((res) => {
      if (res.status === "ok") {
        const account = res.data;
        account.organization = res.data2;
        this.setState({account: account});
      }
    });
  }

  getApplicationObj() {
    return this.state.application;
  }

  parseScopeItems() {
    const scopeStr = this.state.oAuthParams?.scope || "";
    const scopes = scopeStr.split(/[\s,]+/).filter((s) => s !== "");
    if (scopes.length === 0) {
      scopes.push("openid");
    }
    return scopes.map((scope) => {
      const meta = SCOPE_METADATA[scope];
      if (meta) {
        return {scope, icon: meta.icon, title: i18next.t(meta.titleKey), desc: i18next.t(meta.descKey)};
      }
      return {scope, icon: IdcardOutlined, title: scope, desc: i18next.t("consent:Scope unknown desc")};
    });
  }

  buildAuthorizeUrl() {
    const {redirectUri, state} = this.state.oAuthParams;
    const code = this.state.code;
    const sep = redirectUri.includes("?") ? "&" : "?";
    return `${redirectUri}${sep}code=${code}&state=${state}`;
  }

  buildCancelUrl() {
    const {redirectUri, state} = this.state.oAuthParams;
    const sep = redirectUri.includes("?") ? "&" : "?";
    const description = encodeURIComponent(i18next.t("consent:The user denied the access request"));
    return `${redirectUri}${sep}error=access_denied&error_description=${description}&state=${state}`;
  }

  doRedirect(targetUrl) {
    const {noRedirect, redirectUri} = this.state.oAuthParams;
    const {code, state, popup} = this.state;
    if (noRedirect === "true") {
      window.close();
      const newWindow = window.open(targetUrl);
      if (newWindow) {
        setInterval(() => {
          if (!newWindow.closed) {
            newWindow.close();
          }
        }, 1000);
      }
    } else {
      Setting.goToLink(targetUrl);
      if (popup === "1") {
        window.opener.postMessage({type: "loginSuccess", data: {code: code, state: state}}, redirectUri);
      }
    }
  }

  handleAuthorize() {
    this.doRedirect(this.buildAuthorizeUrl());
  }

  handleCancel() {
    this.doRedirect(this.buildCancelUrl());
  }

  renderAppHeader() {
    const application = this.getApplicationObj();
    const displayName = application?.displayName || application?.name || "";
    const logo = application?.logo;
    const isMobile = Setting.isMobile();

    return (
      <div style={{textAlign: "center", marginBottom: 8}}>
        <Avatar
          size={isMobile ? 64 : 72}
          src={logo || undefined}
          style={{backgroundColor: logo ? "transparent" : "#1890ff", verticalAlign: "middle"}}
        >
          {!logo && displayName ? displayName[0] : null}
        </Avatar>
        <Title level={4} style={{marginBottom: 4, marginTop: isMobile ? 12 : 16, fontSize: isMobile ? 18 : 20}}>{displayName}</Title>
        <Text type="secondary" style={{fontSize: isMobile ? 13 : 14}}>
          {i18next.t("consent:wants to access your account")}
        </Text>
        {this.state.account ? (
          <div style={{marginTop: 4}}>
            <Text type="secondary" style={{fontSize: isMobile ? 12 : 13}}>
              {i18next.t("consent:Currently signed in as")}{this.state.account.name}
            </Text>
          </div>
        ) : null}
      </div>
    );
  }

  renderScopeItems() {
    const application = this.getApplicationObj();
    const appName = application?.displayName || application?.name || "";
    const items = this.parseScopeItems();
    const isMobile = Setting.isMobile();

    return (
      <div>
        <Text style={{display: "block", marginBottom: 8, fontSize: isMobile ? 13 : 14}}>
          {i18next.t("consent:This will allow")}「{appName}」{i18next.t("consent:to access")}:
        </Text>
        {items.map((item, idx) => {
          const Icon = item.icon;
          return (
            <div
              key={item.scope}
              style={{
                display: "flex",
                alignItems: "center",
                padding: isMobile ? "12px 0" : "16px 0",
                borderBottom: idx === items.length - 1 ? "none" : "1px solid #f0f0f0",
              }}
            >
              <Avatar size={isMobile ? 36 : 40} icon={<Icon />} style={{backgroundColor: "#e6f7ff", color: "#1890ff", flexShrink: 0}} />
              <div style={{marginLeft: isMobile ? 12 : 16, flex: 1}}>
                <Text strong style={{display: "block", fontSize: isMobile ? 14 : 15}}>{item.title}</Text>
                <Text type="secondary" style={{fontSize: isMobile ? 12 : 13}}>{item.desc}</Text>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    const isMobile = Setting.isMobile();

    if (this.state.invalid) {
      return (
        <div style={{display: "flex", flex: "1", justifyContent: "center", alignItems: "center", padding: isMobile ? "16px" : "0"}}>
          <Card style={{maxWidth: 480, width: "100%", borderRadius: 12, margin: isMobile ? "0 16px" : "0"}}>
            <Result
              status="warning"
              title={i18next.t("consent:Invalid request")}
              subTitle={i18next.t("consent:Invalid request, missing parameter")}
            />
          </Card>
        </div>
      );
    }

    if (this.state.application === undefined) {
      return (
        <div style={{display: "flex", flex: "1", justifyContent: "center", alignItems: "center"}}>
          <Spin size="large" />
        </div>
      );
    }

    if (this.state.application === null) {
      return (
        <div style={{display: "flex", flex: "1", justifyContent: "center", alignItems: "center", padding: isMobile ? "16px" : "0"}}>
          <Card style={{maxWidth: 480, width: "100%", borderRadius: 12, margin: isMobile ? "0 16px" : "0"}}>
            <Result status="error" title={i18next.t("consent:Application not found")} />
          </Card>
        </div>
      );
    }

    return (
      <div style={{
        display: "flex",
        flex: "1",
        justifyContent: "center",
        alignItems: isMobile ? "center" : "flex-start",
        paddingTop: isMobile ? "24px" : "48px",
        paddingBottom: isMobile ? "24px" : "48px",
        paddingLeft: isMobile ? "16px" : "0",
        paddingRight: isMobile ? "16px" : "0",
      }}>
        <Card style={{
          width: isMobile ? "100%" : "480px",
          maxWidth: isMobile ? "100%" : "480px",
          borderRadius: 12,
          boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
          border: "none",
        }}>
          {this.renderAppHeader()}
          <Divider style={{margin: isMobile ? "12px 0" : "16px 0"}} />
          {this.renderScopeItems()}
          <Divider style={{margin: isMobile ? "12px 0" : "16px 0"}} />
          <Row justify="end" gutter={isMobile ? 8 : 12} style={{flexWrap: "wrap"}}>
            <Col>
              <Button size={isMobile ? "middle" : "large"} onClick={() => this.handleCancel()} style={{minWidth: isMobile ? "80px" : "auto"}}>
                {i18next.t("consent:Cancel")}
              </Button>
            </Col>
            <Col>
              <Button type="primary" size={isMobile ? "middle" : "large"} autoFocus onClick={() => this.handleAuthorize()} style={{minWidth: isMobile ? "80px" : "auto"}}>
                {i18next.t("consent:Authorize")}
              </Button>
            </Col>
          </Row>
        </Card>
      </div>
    );
  }
}

export default withRouter(ConsentPage);
